import { useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { withStyles } from 'vopay';
import { usePaylink } from '@/providers/Paylink.provider';
import { useToken } from '@/providers/Token.provider';
import useFetchiQ11AccessTokenInfo from '@/hooks/useApi/useFetchiQ11AccessTokenInfo';
import usePostPaylinkSetAccount from '@/hooks/useApi/usePostPaylinkSetAccount';
import useLinkApplePay from '@/hooks/useApi/useLinkApplePay';
import useStorage from '@/hooks/useStorage';
import useIsApplePayAvailable, { APPLE_PAY_VERSION } from './useIsApplePayAvailable';
import styles from './ApplePayButton.module.scss';

const cn = withStyles(styles);

/**
 * Give the postMessage to the host page a turn of the event loop before navigating. An
 * OuterRedirect replaces the top-level document, which would otherwise drop a message the parent
 * has not processed yet. Mirrors GooglePayButton.
 */
const REDIRECT_MESSAGE_FLUSH_MS = 300;

type ApplePayButtonProps = {
  className?: string;
};

export default function ApplePayButton({ className }: ApplePayButtonProps) {
  const history = useHistory();
  const storage = useStorage();
  const { token } = useToken();
  const { paylink, Token } = usePaylink();
  const { iQ11Token: IframeKey, iQ11TokenInfo } = storage.token.getIQ11Token();
  const { linkApplePay } = useLinkApplePay();
  const [, fetchAccessTokenInfo] = useFetchiQ11AccessTokenInfo({});
  const [, , , setPaylinkAccount] = usePostPaylinkSetAccount({});

  const isApplePayAvailable = useIsApplePayAvailable();
  const buttonRef = useRef<HTMLElement>(null);

  function createPaymentRequest(): ApplePayJS.ApplePayPaymentRequest {
    const label = paylink?.SenderName || 'Total';

    return {
      countryCode: iQ11TokenInfo?.Country || 'CA',
      currencyCode: iQ11TokenInfo?.Currency || 'CAD',
      supportedNetworks: ['amex', 'discover', 'masterCard', 'visa'],
      merchantCapabilities: ['supports3DS'],
      // A connect flow links a card without charging it, so there is no amount to show yet.
      total: Number(paylink?.Amount)
        ? { label, amount: paylink.Amount, type: 'final' }
        : { label, amount: '0.00', type: 'pending' },
      requiredBillingContactFields: ['name'],
      // Apple only populates emailAddress on the shipping contact, never the billing one.
      requiredShippingContactFields: ['email'],
    };
  }

  async function linkPayment(payment: ApplePayJS.ApplePayPayment) {
    const { paymentData, paymentMethod } = payment.token;
    const { givenName, familyName } = payment.billingContact ?? {};
    const cardHolderName = [givenName, familyName].filter(Boolean).join(' ');
    const maskedCardNumber = maskCardNumber(paymentMethod?.displayName);

    const linkRes = await linkApplePay({
      PaymentToken: { token: { paymentData } },
      CardHolderName: cardHolderName,
      MaskedCardNumber: maskedCardNumber,
      CardBrand: paymentMethod?.network || 'unknown',
      Email: payment.shippingContact?.emailAddress || '',
    });

    if (Token) {
      const paylinkRes = await setPaylinkAccount({
        SingleUseToken: Token,
        Iq11Token: linkRes.Token,
        PaymentMethod: 'applepay',
      });

      if (!paylinkRes.Success) throw new Error(paylinkRes.ErrorMessage);

      history.push('/thank-you', {
        paymentMethodType: 'applepay',
        paymentMethod: maskedCardNumber,
        cardNumber: maskedCardNumber,
        cardHolderName,
        cardBrand: paymentMethod?.network,
      });

      return;
    }

    const url = `${token?.RedirectURL || ''}?Token=${linkRes.Token}&PaymentMethod=applepay`;

    const response = {
      Step: 'LINK',
      Url: url,
      Token: linkRes.Token,
      PaymentMethod: 'applepay',
    };

    // Inner and outer redirect
    window.top?.postMessage(response, '*');

    if (window.parent && window.parent !== window.top) {
      window.parent.postMessage(response, '*');
    }

    await flushRedirectMessage();

    if (token?.RedirectMethod === 'InnerRedirect') {
      window.location.href = url;
    } else if (token?.RedirectMethod === 'OuterRedirect') {
      window.top!.location.href = url;
    } else {
      history.push('/success');
    }
  }

  function handleClick() {
    let session: ApplePaySession;

    // Apple throws from the constructor for a payment request it will not accept — an amount it
    // rejects, an unsupported network, a document it is not willing to start a session on. Outside
    // a try/catch that surfaced as the button appearing to do nothing at all.
    try {
      session = new ApplePaySession(APPLE_PAY_VERSION, createPaymentRequest());
    } catch (error) {
      // Safari raises InvalidAccessError here when a session is already open, which a single tap
      // can cause: Apple's <apple-pay-button> retargets the click from the button inside its
      // shadow root, so this runs twice. The first session is open and doing its job, so that
      // throw is not a payment failure. Anything else is.
      if (!isDuplicateSession(error)) {
        console.error(error);
        history.push('/connect/transaction-error');
      }

      return;
    }

    session.onvalidatemerchant = async (event) => {
      try {
        // The merchant session is minted per payment by earthnode over mTLS, so it is fetched
        // here rather than up front with the rest of the page's data. Apple varies the validation
        // host by region and environment, so its URL travels with the request instead of the
        // server assuming one; the server allow-lists it against Apple's published hosts.
        const accessTokenInfo = await fetchAccessTokenInfo({
          IframeKey,
          PaymentSelectionMethod: 'applepay',
          ForOnboarding: false,
          ValidationURL: event.validationURL,
        });

        if (!accessTokenInfo.Success) throw new Error(accessTokenInfo.ErrorMessage);

        session.completeMerchantValidation(accessTokenInfo.AccessToken);
      } catch (error) {
        console.error(error);
        session.abort();
        history.push('/connect/transaction-error');
      }
    };

    session.onpaymentauthorized = async (event) => {
      try {
        await linkPayment(event.payment);
        session.completePayment(ApplePaySession.STATUS_SUCCESS);
      } catch {
        // useLinkApplePay already logged; the sheet and the error page are the user-facing surface.
        session.completePayment(ApplePaySession.STATUS_FAILURE);
        history.push('/connect/transaction-error');
      }
    };

    // begin() throws synchronously when the browser rejects the request (a bad merchant
    // identifier, an unsupported network, a non-https origin). Without this the button looks
    // like it does nothing at all.
    try {
      session.begin();
    } catch (error) {
      if (isDuplicateSession(error)) return;

      console.error(error);
      history.push('/connect/transaction-error');
    }
  }

  useEffect(() => {
    const button = buttonRef.current;

    if (!button) return;

    // Apple's <apple-pay-button> renders its real button inside a shadow root, and the click does
    // not reach React's synthetic handler — the button simply appeared inert. Listen on the host
    // element instead.
    const controller = new AbortController();
    button.addEventListener('click', handleClick, { signal: controller.signal });

    return () => controller.abort();
  });

  if (!isApplePayAvailable) return null;

  return (
    <apple-pay-button
      ref={buttonRef}
      buttonstyle="black"
      type="plain"
      locale="en-US"
      className={cn('button', className)}
    />
  );
}

/**
 * Whether the error is Safari refusing a second payment session on a page that already has one.
 * That is a duplicate invocation rather than a failed payment, so the customer must not be sent
 * anywhere — the session already open is the real one.
 */
function isDuplicateSession(error: unknown) {
  return (error as Error)?.name === 'InvalidAccessError';
}

/**
 * Apple never exposes the card number — the only hint is a display name like "Visa 1234". Pad
 * the last four to the same masked shape the Google Pay path stores.
 */
function maskCardNumber(displayName?: string) {
  const lastFour = displayName?.match(/(\d{4})\s*$/)?.[1];

  return lastFour ? lastFour.padStart(16, 'X') : '';
}

async function flushRedirectMessage() {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, REDIRECT_MESSAGE_FLUSH_MS);
  });
}
